import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { clearLocalStorage, clearSessionStorage, fetchLocalStorageItem } from '../Utils/StorageUtils';
import { getUrlSelectedCardId } from '../Utils/CommonFunctions';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// A `?token=` in the URL (SDK/iframe bootstrap, or a PayU/payment-return
// redirect carrying the token) must authenticate the tab on its very first
// render. Doing this in a useEffect is too late: PrivateRoute's <Navigate>
// to /auth fires from its own effect before AppWithState's token-handling
// effect runs, so the tab bounces to the login screen even though a valid
// token is sitting right there in the URL (this is what forced re-login in
// popup windows, since popups don't share the iframe's partitioned storage).
const getBootstrapTokenFromUrl = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('token');
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const bootstrapToken = getBootstrapTokenFromUrl();
    if (bootstrapToken) {
      localStorage.setItem('token', bootstrapToken);
      return true;
    }
    // Initialize isAuthenticated from localStorage
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(() => {
    // Check if we should show signup after redirect
    const shouldShow = localStorage.getItem('showSignUpAfterRedirect') === 'true';
    if (shouldShow) {
      localStorage.removeItem('showSignUpAfterRedirect'); // Clean up the flag
    }
    return shouldShow;
  });
  const [cardInfo, setCardInfo] = useState(null);
  const [windowLocationUrl, setWindowLocationUrl] = useState("");
  const [subVarientIdGlobal, setSubVarientIdGlobal] = useState(() => {
    const urlCardId = getUrlSelectedCardId();
    if (urlCardId !== null) return urlCardId;
    const subId = localStorage.getItem('subId');
    return subId ? parseInt(subId) : 0;
  });
  const [userData, setUserData] = useState( () =>{
    const localUserData = localStorage.getItem('userData');
    return localUserData ? JSON.parse(localUserData) : {};
  });
  const [selectedCardData, setSelectedCardData] = useState(() => {
    const savedCard = localStorage.getItem('selectedCard');
    if (!savedCard || savedCard === "undefined") return {};
    const parsedCard = JSON.parse(savedCard);
    const urlCardId = getUrlSelectedCardId();
    if (urlCardId !== null && Number(parsedCard?.sub_variant?.id) !== urlCardId) {
      return {};
    }
    return parsedCard;
  });

  const [iscardChange, setIsCardChange] = useState(false);
  // Initialize userLocation from localStorage (if available)
  const [userLocation, setUserLocation] = useState(() => {
    const storedLocation = localStorage.getItem('userLocation');
    return storedLocation && storedLocation !== "undefined"
      ? JSON.parse(storedLocation) : { latitude: null, longitude: null };
  });
  const [integratorTheme, setIntegratorTheme] = useState(null);

  const [creditCard, setCreditCard] = useState(() => {
    const savedCard = localStorage.getItem('creditCards');
    return savedCard && savedCard !== "undefined" ? JSON.parse(savedCard) : [];
  });

  const [moduleInfo, setModuleInfo] = useState([]);
  const [allSubModuleInfo, setAllSubModuleInfo] = useState([]);
  const [websiteInfo, setWebsiteInfo] = useState({});
  const [websiteLandingPageInfo, setWebsiteLandingPageInfo] = useState({});
  const [loyaltyPointData, setLoyaltyPointData] = useState({});
  const [headerData, setHeaderData] = useState({});
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [showWhatsAppOptIn, setShowWhatsAppOptIn] = useState(fetchLocalStorageItem('showWhatsAppOptIn') === 'true');

  // Add a force update mechanism
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showNameModal, setShowNameModal] = useState(false);
  // Add a method to trigger updates across all components
  const triggerGlobalUpdate = useCallback(() => {
    setForceUpdate(prev => prev + 1);
  }, []);

  useEffect(() => {
    localStorage.setItem('isAuthenticated', isAuthenticated);
  }, [isAuthenticated]);

  const updateCreditCard = (newCreditCards) => {
    setCreditCard(newCreditCards);
    localStorage.setItem('creditCards', JSON.stringify(newCreditCards));
  };

  const updateSelectedCard = (Card) => {
    setSelectedCardData(Card);
    localStorage.setItem('selectedCard', JSON.stringify(Card));
    setIsCardChange(prev => !prev);
  };

  // Enhanced subvariant update
  const updateSubVariantGlobal = useCallback((id) => {
    setSubVarientIdGlobal(id);
    localStorage.setItem('subId', id);
    setIsCardChange(prev => !prev); // Force re-render
  }, []);

  useEffect(() => {
    localStorage.setItem('subId', JSON.stringify(subVarientIdGlobal));
  }, [subVarientIdGlobal]);

  // Update localStorage whenever userLocation changes
  useEffect(() => {
    if (userLocation.latitude && userLocation.longitude) {
      localStorage.setItem('userLocation', JSON.stringify(userLocation));
    }
  }, [userLocation]);

  // Listen for force logout event from API
  useEffect(() => {
    const handleForceLogout = () => {
      logout();
      // Don't set showSignUp here since it's handled by localStorage
    };

    window.addEventListener('forceLogout', handleForceLogout);
    
    return () => {
      window.removeEventListener('forceLogout', handleForceLogout);
    };
  }, []);

  const login = (token) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    // Trigger a global update
    triggerGlobalUpdate();
  };

  const logout = (isFullPage = false) => {
    if (isFullPage) {
      window.location.href = "/auth";
    } else {
      window.location.href = "/";
    }
    clearLocalStorage();
    clearSessionStorage();
    setIsAuthenticated(false);
    setUserData({});
    setSelectedCardData({});
    setCreditCard([]);
    setSubVarientIdGlobal(0);
    setShowLogin(false);
    setShowSignUp(false);
  };

  return (
    <AuthContext.Provider
      value={{
        moduleInfo, setModuleInfo, subVarientIdGlobal, setUserData, userData, setSubVarientIdGlobal, isAuthenticated,
        setCardInfo, setIsCardChange, iscardChange, cardInfo, login, logout, showLogin, setShowLogin, setUserLocation,
        userLocation, selectedCardData, setSelectedCardData, updateSelectedCard, setWindowLocationUrl, windowLocationUrl,
        setIntegratorTheme, integratorTheme, setIsAuthenticated, setCreditCard, updateCreditCard, creditCard,
        setAllSubModuleInfo, allSubModuleInfo, setShowSignUp, showSignUp, websiteInfo, setWebsiteInfo, websiteLandingPageInfo,
        setWebsiteLandingPageInfo, headerData, setHeaderData, loyaltyPointData, setLoyaltyPointData, triggerGlobalUpdate, 
        updateSubVariantGlobal, showPasskeyModal, setShowPasskeyModal, forceUpdate, setShowNameModal, showNameModal, 
        setShowWhatsAppOptIn, showWhatsAppOptIn
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
